import { AuditLog, IAuditChange } from "../interfaces/models/IAudityLogs";
import {
  attendeesStatusEnum,
  attendeesTypeEnum,
  paymentMethodEnum,
  statusEnum,
} from "../Enums/RegistrationEnum";
import { formatTimestampDate } from "./dates";
import { formatAuditActorDisplay } from "./auditActorDisplay";
import { normalizeSpecialEventGuestNames } from "./specialEventGuestNames";

export type AuditActorDisplayContext = {
  registrantDisplayName?: string;
  registrantUserId?: string;
};

function findChange(
  changes: IAuditChange[] | undefined,
  field: string,
): IAuditChange | undefined {
  return changes?.find((c) => c.field === field);
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (
    typeof value === "string" &&
    value.trim() &&
    !Number.isNaN(Number(value))
  ) {
    return Number(value);
  }
  return null;
}

function formatAuditDate(timestamp?: string): string {
  if (!timestamp) {
    return "";
  }
  const ms = Date.parse(timestamp);
  if (Number.isNaN(ms)) {
    return "";
  }
  return formatTimestampDate(Math.floor(ms / 1000));
}

function formatAuditDateTime(timestamp?: string): string {
  if (!timestamp) {
    return "";
  }
  const ms = Date.parse(timestamp);
  if (Number.isNaN(ms)) {
    return "";
  }
  return new Date(ms).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type GuestAuditSnapshot = {
  id?: string;
  name: string;
  specialRequest: string;
  registrationDate: number | null;
};

function readGuestSnapshot(value: unknown): GuestAuditSnapshot | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const rec = value as Record<string, unknown>;
  const name = typeof rec.name === "string" ? rec.name.trim() : "";
  const specialRequest =
    typeof rec.specialRequest === "string" ? rec.specialRequest.trim() : "";
  const id = typeof rec.id === "string" ? rec.id.trim() : "";
  if (!name && !specialRequest && !id) {
    return null;
  }
  return {
    id: id || undefined,
    name,
    specialRequest,
    registrationDate: asNumber(rec.registrationDate),
  };
}

function guestSnapshotsFromValue(value: unknown): GuestAuditSnapshot[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map(readGuestSnapshot)
    .filter((guest): guest is GuestAuditSnapshot => guest != null);
}

function guestNamesFromValue(value: unknown): string[] {
  return guestSnapshotsFromValue(value)
    .map((guest) => guest.name)
    .filter(Boolean);
}

function guestDisplayName(guest: GuestAuditSnapshot): string {
  return guest.name || "guest";
}

type GuestChangePhrases = {
  added: (name: string) => string;
  deleted: (name: string) => string;
  renamed: (from: string, to: string) => string;
  specialRequestAdded: (name: string) => string;
  specialRequestRemoved: (name: string) => string;
  specialRequestUpdated: (name: string) => string;
  registrationDateUpdated: (name: string) => string;
  updated: (names: string) => string;
};

function describeGuestCollectionChanges(
  previous: GuestAuditSnapshot[],
  next: GuestAuditSnapshot[],
  phrases: GuestChangePhrases,
): string[] {
  const previousLeft = previous.map((guest) => ({ guest, used: false }));
  const nextLeft = next.map((guest) => ({ guest, used: false }));
  const pairs: Array<{
    before: GuestAuditSnapshot;
    after: GuestAuditSnapshot;
  }> = [];

  const pairBy = (
    matches: (before: GuestAuditSnapshot, after: GuestAuditSnapshot) => boolean,
  ) => {
    for (const nextItem of nextLeft) {
      if (nextItem.used) {
        continue;
      }
      const previousItem = previousLeft.find(
        (item) => !item.used && matches(item.guest, nextItem.guest),
      );
      if (!previousItem) {
        continue;
      }
      previousItem.used = true;
      nextItem.used = true;
      pairs.push({ before: previousItem.guest, after: nextItem.guest });
    }
  };

  pairBy(
    (before, after) =>
      Boolean(before.id) && Boolean(after.id) && before.id === after.id,
  );
  pairBy(
    (before, after) => Boolean(before.name) && before.name === after.name,
  );

  const parts: string[] = [];
  for (const { before, after } of pairs) {
    const name = guestDisplayName(after);
    if (before.name !== after.name) {
      parts.push(
        phrases.renamed(guestDisplayName(before), guestDisplayName(after)),
      );
    }
    if (before.specialRequest !== after.specialRequest) {
      if (!before.specialRequest && after.specialRequest) {
        parts.push(phrases.specialRequestAdded(name));
      } else if (before.specialRequest && !after.specialRequest) {
        parts.push(phrases.specialRequestRemoved(name));
      } else {
        parts.push(phrases.specialRequestUpdated(name));
      }
    }
    if (
      before.registrationDate !== after.registrationDate &&
      before.registrationDate != null &&
      after.registrationDate != null
    ) {
      parts.push(phrases.registrationDateUpdated(name));
    }
  }

  for (const guest of nextLeft) {
    if (!guest.used) {
      parts.push(phrases.added(guestDisplayName(guest.guest)));
    }
  }
  for (const guest of previousLeft) {
    if (!guest.used) {
      parts.push(phrases.deleted(guestDisplayName(guest.guest)));
    }
  }

  if (
    parts.length === 0 &&
    JSON.stringify(previous) !== JSON.stringify(next)
  ) {
    const names = next.map(guestDisplayName).filter(Boolean).join(", ");
    if (names) {
      parts.push(phrases.updated(names));
    }
  }

  return parts;
}

const MAIN_GUEST_PHRASES: GuestChangePhrases = {
  added: (name) => `Guest added: ${name}`,
  deleted: (name) => `Guest deleted: ${name}`,
  renamed: (from, to) => `Guest updated: ${from} → ${to}`,
  specialRequestAdded: (name) => `Guest special request added: ${name}`,
  specialRequestRemoved: (name) => `Guest special request removed: ${name}`,
  specialRequestUpdated: (name) => `Guest special request updated: ${name}`,
  registrationDateUpdated: (name) => `Guest registration date updated: ${name}`,
  updated: (names) => `Guest updated: ${names}`,
};

const SPECIAL_EVENT_GUEST_PHRASES: GuestChangePhrases = {
  added: (name) => `Guest added to special event: ${name}`,
  deleted: (name) => `Guest deleted from special event: ${name}`,
  renamed: (from, to) => `Guest updated on special event: ${from} → ${to}`,
  specialRequestAdded: (name) =>
    `Guest special request added to special event: ${name}`,
  specialRequestRemoved: (name) =>
    `Guest special request removed from special event: ${name}`,
  specialRequestUpdated: (name) =>
    `Guest special request updated on special event: ${name}`,
  registrationDateUpdated: (name) =>
    `Guest registration date updated on special event: ${name}`,
  updated: (names) => `Guest updated on special event: ${names}`,
};

function actorLabel(
  log: AuditLog,
  actorContext?: AuditActorDisplayContext,
): string {
  return formatAuditActorDisplay(log, actorContext);
}

function labelRegistrationStatus(value: unknown): string {
  const key = String(value ?? "").trim();
  if (!key) {
    return "—";
  }
  return (
    (attendeesStatusEnum as Record<string, string>)[key] ??
    key.charAt(0).toUpperCase() + key.slice(1)
  );
}

function hasAuditTimestamp(value: unknown): boolean {
  if (value == null || value === "") {
    return false;
  }
  const numeric = asNumber(value);
  return numeric != null && numeric > 0;
}

function labelRegistrantType(value: unknown): string {
  const key = String(value ?? "").trim();
  if (!key) {
    return "—";
  }
  return (attendeesTypeEnum as Record<string, string>)[key] ?? key;
}

function labelPaymentMethod(value: unknown): string {
  const key = String(value ?? "").trim();
  if (!key) {
    return "—";
  }
  return (paymentMethodEnum as Record<string, string>)[key] ?? key;
}

function labelPaymentStatus(value: unknown): string {
  const key = String(value ?? "").trim();
  if (!key) {
    return "—";
  }
  return (statusEnum as Record<string, string>)[key] ?? key;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((v) => String(v ?? "").trim()).filter(Boolean);
}

function specialEventGuestsByProgram(
  value: unknown,
): Record<string, GuestAuditSnapshot[]> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  const raw = value as Record<string, unknown>;
  const first = Object.values(raw)[0];
  if (Array.isArray(first)) {
    const result: Record<string, GuestAuditSnapshot[]> = {};
    for (const [programId, guests] of Object.entries(raw)) {
      const snapshots = guestSnapshotsFromValue(guests);
      if (snapshots.length > 0) {
        result[programId] = snapshots;
      }
    }
    if (Object.keys(result).length > 0) {
      return result;
    }
  }
  const namesByProgram = normalizeSpecialEventGuestNames(raw);
  return Object.fromEntries(
    Object.entries(namesByProgram).map(([programId, names]) => [
      programId,
      names.map((name) => ({
        name,
        specialRequest: "",
        registrationDate: null,
      })),
    ]),
  );
}

function describeTextFieldChange(
  label: string,
  change: IAuditChange | undefined,
): string | null {
  if (!change) {
    return null;
  }
  const oldText =
    typeof change.oldValue === "string" ? change.oldValue.trim() : "";
  const newText =
    typeof change.newValue === "string" ? change.newValue.trim() : "";
  if (oldText === newText) {
    return null;
  }
  if (!oldText && newText) {
    return `${label} added`;
  }
  if (oldText && !newText) {
    return `${label} removed`;
  }
  return `${label} updated`;
}

function describeMainGuestChanges(
  changes: IAuditChange[] | undefined,
): string[] {
  const parts: string[] = [];
  const guestsChange = findChange(changes, "guests");
  if (guestsChange) {
    parts.push(
      ...describeGuestCollectionChanges(
        guestSnapshotsFromValue(guestsChange.oldValue),
        guestSnapshotsFromValue(guestsChange.newValue),
        MAIN_GUEST_PHRASES,
      ),
    );
    return parts;
  }

  for (const field of ["guestName", "guestName2"] as const) {
    const change = findChange(changes, field);
    if (!change) {
      continue;
    }
    const oldName =
      typeof change.oldValue === "string" ? change.oldValue.trim() : "";
    const newName =
      typeof change.newValue === "string" ? change.newValue.trim() : "";
    if (oldName === newName) {
      continue;
    }
    if (!oldName && newName) {
      parts.push(`Guest added: ${newName}`);
    }
    else if (oldName && !newName) {
      parts.push(`Guest deleted: ${oldName}`);
    }
    else {
      parts.push(`Guest updated: ${oldName} → ${newName}`);
    }
  }
  return parts;
}

function describeSpecialEventGuestChanges(
  changes: IAuditChange[] | undefined,
): string[] {
  const parts: string[] = [];
  const richChange = findChange(changes, "specialEventGuests");
  const namesChange = findChange(changes, "specialEventGuestNames");
  const change = richChange ?? namesChange;
  if (!change) {
    return parts;
  }

  const prev = specialEventGuestsByProgram(change.oldValue);
  const next = specialEventGuestsByProgram(change.newValue);
  const programIds = new Set([...Object.keys(prev), ...Object.keys(next)]);

  for (const programId of programIds) {
    parts.push(
      ...describeGuestCollectionChanges(
        prev[programId] ?? [],
        next[programId] ?? [],
        SPECIAL_EVENT_GUEST_PHRASES,
      ),
    );
  }
  return parts;
}

function describeSpecialEventSelectionChanges(
  changes: IAuditChange[] | undefined,
  programTitles?: Record<string, string>,
): string[] {
  const change = findChange(changes, "selectedSpecialEventIds");
  if (!change) {
    return [];
  }
  const prev = new Set(asStringArray(change.oldValue));
  const next = new Set(asStringArray(change.newValue));
  const parts: string[] = [];
  for (const id of next) {
    if (!prev.has(id)) {
      const title = programTitles?.[id]?.trim() || id;
      parts.push(`Special event added: ${title}`);
    }
  }
  for (const id of prev) {
    if (!next.has(id)) {
      const title = programTitles?.[id]?.trim() || id;
      parts.push(`Special event removed: ${title}`);
    }
  }
  return parts;
}

function describeRegistrationLifecycle(
  log: AuditLog,
  changes: IAuditChange[] | undefined,
): string | null {
  if (String(log.action ?? "").toUpperCase() === "DELETE") {
    return "Registration deleted";
  }

  const cancelDateChange = findChange(changes, "cancelRegistrationDate");
  if (cancelDateChange) {
    const wasCancelled = hasAuditTimestamp(cancelDateChange.oldValue);
    const isCancelled = hasAuditTimestamp(cancelDateChange.newValue);
    if (!wasCancelled && isCancelled) {
      return "Registration cancelled";
    }
    if (wasCancelled && !isCancelled) {
      return "Registration restored";
    }
  }

  const statusChange = findChange(changes, "status");
  if (!statusChange) {
    return null;
  }
  const fromStatus = String(statusChange.oldValue ?? "").trim().toLowerCase();
  const toStatus = String(statusChange.newValue ?? "").trim().toLowerCase();
  if (toStatus === "cancelled" && fromStatus !== "cancelled") {
    return "Registration cancelled";
  }
  if (fromStatus === "cancelled" && toStatus !== "cancelled") {
    return "Registration restored";
  }
  return null;
}

function auditText(value: unknown): string {
  if (typeof value === "string") {
    return value.trim();
  }
  return "";
}

function describeCreatedRegistrationFields(
  changes: IAuditChange[] | undefined,
  options?: { programTitles?: Record<string, string> },
): string[] {
  const parts: string[] = [];
  const push = (label: string, value: string) => {
    if (!value || value === "—") {
      return;
    }
    parts.push(`${label}: ${value}`);
  };

  push("Firm", auditText(findChange(changes, "firmName")?.newValue));

  const name = [
    auditText(findChange(changes, "personName")?.newValue),
    auditText(findChange(changes, "personLastName")?.newValue),
  ]
    .filter(Boolean)
    .join(" ");
  push("Name", name);

  push("Badge Name", auditText(findChange(changes, "badgeName")?.newValue));

  const registrationDate = asNumber(
    findChange(changes, "registrationDate")?.newValue,
  );
  if (registrationDate != null) {
    push("Registrant Date", formatTimestampDate(registrationDate));
  }

  push(
    "Special Requests",
    auditText(findChange(changes, "specialRequests")?.newValue),
  );
  push(
    "Notes",
    auditText(findChange(changes, "note")?.newValue) ||
      auditText(findChange(changes, "registrationNotes")?.newValue),
  );

  const guestNames = guestSnapshotsFromValue(
    findChange(changes, "guests")?.newValue,
  )
    .map((guest) => guest.name)
    .filter(Boolean);
  if (guestNames.length === 0) {
    for (const field of ["guestName", "guestName2"] as const) {
      const guestName = auditText(findChange(changes, field)?.newValue);
      if (guestName) {
        guestNames.push(guestName);
      }
    }
  }
  if (guestNames.length > 0) {
    push("Guests", guestNames.join(", "));
  }

  const paymentMethod = findChange(changes, "paymentMethod")?.newValue;
  if (paymentMethod != null && String(paymentMethod).trim()) {
    push("Payment Method", labelPaymentMethod(paymentMethod));
  }

  const additionalFee = asNumber(findChange(changes, "additionalFee")?.newValue);
  if (additionalFee != null && additionalFee !== 0) {
    push("Additional Fees", `$${additionalFee}`);
  }

  const programIds = asStringArray(
    findChange(changes, "selectedSpecialEventIds")?.newValue,
  );
  if (programIds.length > 0) {
    const titles = programIds.map(
      (id) => options?.programTitles?.[id]?.trim() || id,
    );
    push("Special Events", titles.join(", "));
  }

  return parts;
}

function describeRegistrantLogChanges(
  log: AuditLog,
  options?: { programTitles?: Record<string, string> },
): string[] {
  if (String(log.action ?? "").toUpperCase() === "CREATE") {
    const created = describeCreatedRegistrationFields(log.changes, options);
    return created.length > 0 ? created : ["Registration created"];
  }

  const changes = log.changes;
  const lifecycle = describeRegistrationLifecycle(log, changes);
  if (lifecycle === "Registration deleted") {
    return [lifecycle];
  }

  const parts: string[] = [];
  if (lifecycle) {
    parts.push(lifecycle);
  }

  const statusChange = findChange(changes, "status");
  if (statusChange && !lifecycle) {
    const fromStatus = labelRegistrationStatus(statusChange.oldValue);
    const toStatus = labelRegistrationStatus(statusChange.newValue);
    parts.push(`Registration status changed: ${fromStatus} → ${toStatus}`);
  }

  const typeChange = findChange(changes, "attendeeType");
  if (typeChange) {
    parts.push(
      `Registrant Type changed: ${labelRegistrantType(typeChange.newValue)}`,
    );
  }

  const badgeChange = findChange(changes, "badgeName");
  if (badgeChange) {
    const newBadge =
      typeof badgeChange.newValue === "string"
        ? badgeChange.newValue.trim()
        : "";
    const oldBadge =
      typeof badgeChange.oldValue === "string"
        ? badgeChange.oldValue.trim()
        : "";
    if (oldBadge !== newBadge) {
      parts.push(newBadge ? `Badge changed: ${newBadge}` : "Badge removed");
    }
  }

  const specialRequestPart = describeTextFieldChange(
    "Special request",
    findChange(changes, "specialRequests"),
  );
  if (specialRequestPart) {
    parts.push(specialRequestPart);
  }

  const notesPart = describeTextFieldChange(
    "Notes",
    findChange(changes, "note") ?? findChange(changes, "registrationNotes"),
  );
  if (notesPart) {
    parts.push(notesPart);
  }

  const additionalFeeChange = findChange(changes, "additionalFee");
  if (additionalFeeChange) {
    const oldFee = asNumber(additionalFeeChange.oldValue) ?? 0;
    const newFee = asNumber(additionalFeeChange.newValue) ?? 0;
    if (oldFee !== newFee) {
      parts.push(`Additional Fees changed: $${oldFee} → $${newFee}`);
    }
  }

  const paymentMethodChange = findChange(changes, "paymentMethod");
  if (paymentMethodChange) {
    const fromMethod = labelPaymentMethod(paymentMethodChange.oldValue);
    const toMethod = labelPaymentMethod(paymentMethodChange.newValue);
    if (fromMethod !== toMethod) {
      parts.push(`Payment method changed: ${fromMethod} → ${toMethod}`);
    }
  }

  parts.push(...describeMainGuestChanges(changes));
  parts.push(
    ...describeSpecialEventSelectionChanges(changes, options?.programTitles),
  );
  parts.push(...describeSpecialEventGuestChanges(changes));

  return parts;
}

const REGISTRANT_HISTORY_EXACT: Record<string, { type: string; info: string }> = {
  "Registration created": { type: "Registration", info: "created" },
  "Registration deleted": { type: "Registration", info: "deleted" },
  "Registration cancelled": { type: "Registration", info: "cancelled" },
  "Registration restored": { type: "Registration", info: "restored" },
  "Badge removed": { type: "Badge", info: "removed" },
  "Special request added": { type: "Special request", info: "added" },
  "Special request removed": { type: "Special request", info: "removed" },
  "Special request updated": { type: "Special request", info: "updated" },
  "Notes added": { type: "Notes", info: "added" },
  "Notes removed": { type: "Notes", info: "removed" },
  "Notes updated": { type: "Notes", info: "updated" },
};

const REGISTRANT_HISTORY_PREFIXES: Array<{
  prefix: string;
  type: string;
  action: string;
}> = [
  { prefix: "Guest special request added to special event: ", type: "Special event guest", action: "special request added" },
  { prefix: "Guest special request removed from special event: ", type: "Special event guest", action: "special request removed" },
  { prefix: "Guest special request updated on special event: ", type: "Special event guest", action: "special request updated" },
  { prefix: "Guest registration date updated on special event: ", type: "Special event guest", action: "registration date updated" },
  { prefix: "Guest updated on special event: ", type: "Special event guest", action: "updated" },
  { prefix: "Guest added to special event: ", type: "Special event guest", action: "added" },
  { prefix: "Guest deleted from special event: ", type: "Special event guest", action: "deleted" },
  { prefix: "Guest special request added: ", type: "Guest", action: "special request added" },
  { prefix: "Guest special request removed: ", type: "Guest", action: "special request removed" },
  { prefix: "Guest special request updated: ", type: "Guest", action: "special request updated" },
  { prefix: "Guest registration date updated: ", type: "Guest", action: "registration date updated" },
  { prefix: "Guest added: ", type: "Guest", action: "added" },
  { prefix: "Guest deleted: ", type: "Guest", action: "deleted" },
  { prefix: "Guest updated: ", type: "Guest", action: "updated" },
  { prefix: "Special event added: ", type: "Special event", action: "added" },
  { prefix: "Special event removed: ", type: "Special event", action: "removed" },
  { prefix: "Registration status changed: ", type: "Registration status", action: "changed" },
  { prefix: "Registrant Type changed: ", type: "Registrant Type", action: "changed" },
  { prefix: "Additional Fees changed: ", type: "Additional Fees", action: "changed" },
  { prefix: "Badge changed: ", type: "Badge", action: "changed" },
  { prefix: "Payment method changed: ", type: "Payment method", action: "changed" },
];

function splitRegistrantHistoryPart(part: string): { type: string; info: string } {
  const exact = REGISTRANT_HISTORY_EXACT[part];
  if (exact) {
    return exact;
  }

  for (const rule of REGISTRANT_HISTORY_PREFIXES) {
    if (part.startsWith(rule.prefix)) {
      const detail = part.slice(rule.prefix.length).trim();
      return {
        type: rule.type,
        info: detail ? `${rule.action}: ${detail}` : rule.action,
      };
    }
  }

  return { type: "Update", info: part };
}

export interface RegistrationRegistrantHistoryRow {
  id: string;
  type: string;
  info: string;
  modifiedBy: string;
  date: string;
  modified: string;
  timestamp: string;
}

/**
 * Registrant type in effect for each log. Later edits often omit attendeeType,
 * so the value has to be carried forward from earlier create/update logs.
 */
function resolveRegistrantTypeByLog(
  logs: AuditLog[],
  fallbackAttendeeType?: string | null,
): Map<string, string> {
  const chronological = [...logs].sort((a, b) =>
    (a.timestamp ?? "").localeCompare(b.timestamp ?? ""),
  );
  const typeByLog = new Map<string, string>();
  let running = "";

  for (const log of chronological) {
    const typeChange = findChange(log.changes, "attendeeType");
    if (typeChange) {
      const next = labelRegistrantType(typeChange.newValue);
      if (next && next !== "—") {
        running = next;
      }
    }
    typeByLog.set(log.id, running);
  }

  const fallback =
    fallbackAttendeeType != null && String(fallbackAttendeeType).trim()
      ? labelRegistrantType(fallbackAttendeeType)
      : "";
  const fillValue =
    [...typeByLog.values()].find((value) => Boolean(value)) ||
    fallback ||
    "—";

  for (const log of chronological) {
    if (!typeByLog.get(log.id)) {
      typeByLog.set(log.id, fillValue);
    }
  }

  return typeByLog;
}

export function projectRegistrationRegistrantHistory(
  logs: AuditLog[],
  actorContext?: AuditActorDisplayContext,
  options?: {
    programTitles?: Record<string, string>;
    fallbackAttendeeType?: string | null;
  },
): RegistrationRegistrantHistoryRow[] {
  const registrantTypeByLog = resolveRegistrantTypeByLog(
    logs,
    options?.fallbackAttendeeType,
  );
  const rows: RegistrationRegistrantHistoryRow[] = [];
  for (const log of logs) {
    const parts = describeRegistrantLogChanges(log, options);
    if (parts.length === 0) {
      continue;
    }
    rows.push({
      id: log.id,
      type: registrantTypeByLog.get(log.id) || "—",
      info: parts.join(", "),
      modifiedBy: actorLabel(log, actorContext),
      date: formatAuditDate(log.timestamp),
      modified: formatAuditDateTime(log.timestamp),
      timestamp: log.timestamp ?? "",
    });
  }
  return rows;
}

export interface RegistrationStatusHistoryRow {
  id: string;
  fromStatus: string;
  toStatus: string;
  changedBy: string;
  date: string;
}

export function projectRegistrationStatusHistory(
  logs: AuditLog[],
  actorContext?: AuditActorDisplayContext,
): RegistrationStatusHistoryRow[] {
  const rows: RegistrationStatusHistoryRow[] = [];
  for (const log of logs) {
    const statusChange = findChange(log.changes, "status");
    if (!statusChange) {
      continue;
    }
    rows.push({
      id: log.id,
      fromStatus: labelRegistrationStatus(statusChange.oldValue),
      toStatus: labelRegistrationStatus(statusChange.newValue),
      changedBy: actorLabel(log, actorContext),
      date: formatAuditDate(log.timestamp),
    });
  }
  return rows;
}

export interface RegistrationPaymentHistoryRow {
  id: string;
  amount: number;
  note: string;
  savedBy: string;
  paymentMethod: string;
  paymentStatus: string;
  date: string;
}

/**
 * Payment method in effect for each log: a payment row is often saved after the
 * method was already set (e.g. registration created as prepaid, payment posted next),
 * so the method has to be carried forward from earlier logs.
 */
function resolvePaymentMethodByLog(logs: AuditLog[]): Map<string, string> {
  const chronological = [...logs].sort((a, b) =>
    (a.timestamp ?? "").localeCompare(b.timestamp ?? ""),
  );
  const methodByLog = new Map<string, string>();
  let currentMethod = "";
  for (const log of chronological) {
    const methodChange = findChange(log.changes, "paymentMethod");
    if (methodChange) {
      currentMethod = String(methodChange.newValue ?? "").trim();
    }
    methodByLog.set(log.id, currentMethod);
  }
  return methodByLog;
}

/**
 * Payment status in effect for each log. Later payment rows often omit
 * paymentStatus, so the value is carried forward from earlier logs.
 */
function resolvePaymentStatusByLog(
  logs: AuditLog[],
  fallbackPaymentStatus?: string | null,
): Map<string, string> {
  const chronological = [...logs].sort((a, b) =>
    (a.timestamp ?? "").localeCompare(b.timestamp ?? ""),
  );
  const statusByLog = new Map<string, string>();
  let running = "";

  for (const log of chronological) {
    const statusChange = findChange(log.changes, "paymentStatus");
    if (statusChange) {
      const next = labelPaymentStatus(statusChange.newValue);
      if (next && next !== "—") {
        running = next;
      }
    }
    statusByLog.set(log.id, running);
  }

  const fallback =
    fallbackPaymentStatus != null && String(fallbackPaymentStatus).trim()
      ? labelPaymentStatus(fallbackPaymentStatus)
      : "";
  const fillValue =
    [...statusByLog.values()].find((value) => Boolean(value)) ||
    fallback ||
    "—";

  for (const log of chronological) {
    if (!statusByLog.get(log.id)) {
      statusByLog.set(log.id, fillValue);
    }
  }

  return statusByLog;
}

export function projectRegistrationPaymentHistory(
  logs: AuditLog[],
  actorContext?: AuditActorDisplayContext,
  options?: { fallbackPaymentStatus?: string | null },
): RegistrationPaymentHistoryRow[] {
  const methodByLog = resolvePaymentMethodByLog(logs);
  const statusByLog = resolvePaymentStatusByLog(
    logs,
    options?.fallbackPaymentStatus,
  );
  const rows: RegistrationPaymentHistoryRow[] = [];
  for (const log of logs) {
    const amountChange = findChange(log.changes, "amountPaid");
    const noteChange = findChange(log.changes, "amountPaidNote");

    const oldAmount = asNumber(amountChange?.oldValue) ?? 0;
    const newAmount = asNumber(amountChange?.newValue);
    const delta =
      newAmount != null ? Number((newAmount - oldAmount).toFixed(2)) : 0;

    // Only actual money movement (or an explicit payment note) is a payment row;
    // a payment-method change on its own is registrant history, not a payment.
    if (delta === 0 && !noteChange) {
      continue;
    }

    rows.push({
      id: log.id,
      amount: delta,
      note: noteChange ? String(noteChange.newValue ?? "") : "",
      savedBy: actorLabel(log, actorContext),
      paymentMethod: methodByLog.get(log.id) ?? "",
      paymentStatus: statusByLog.get(log.id) || "—",
      date: formatAuditDate(log.timestamp),
    });
  }
  return rows;
}

export interface RegistrationGuestHistoryRow {
  id: string;
  guest: string;
  action: string;
  changedBy: string;
  date: string;
}

export function projectRegistrationGuestHistory(
  logs: AuditLog[],
  actorContext?: AuditActorDisplayContext,
): RegistrationGuestHistoryRow[] {
  const rows: RegistrationGuestHistoryRow[] = [];
  for (const log of logs) {
    const guestsChange = findChange(log.changes, "guests");
    const g1 = findChange(log.changes, "guestName");
    const g2 = findChange(log.changes, "guestName2");
    const changedBy = actorLabel(log, actorContext);

    if (guestsChange) {
      const prev = guestNamesFromValue(guestsChange.oldValue);
      const next = guestNamesFromValue(guestsChange.newValue);
      const prevSet = new Set(prev);
      const nextSet = new Set(next);
      for (const name of next) {
        if (!prevSet.has(name)) {
          rows.push({
            id: `${log.id}-add-${name}`,
            guest: name,
            action: "Added",
            changedBy,
            date: formatAuditDate(log.timestamp),
          });
        }
      }
      for (const name of prev) {
        if (!nextSet.has(name)) {
          rows.push({
            id: `${log.id}-del-${name}`,
            guest: name,
            action: "Removed",
            changedBy,
            date: formatAuditDate(log.timestamp),
          });
        }
      }
      if (
        prev.join("\0") === next.join("\0") &&
        JSON.stringify(guestsChange.oldValue) !==
          JSON.stringify(guestsChange.newValue)
      ) {
        rows.push({
          id: `${log.id}-edit`,
          guest: next.join(", ") || prev.join(", "),
          action: "Updated",
          changedBy,
          date: formatAuditDate(log.timestamp),
        });
      }
      continue;
    }

    for (const change of [g1, g2]) {
      if (!change) {
        continue;
      }
      const oldName =
        typeof change.oldValue === "string" ? change.oldValue.trim() : "";
      const newName =
        typeof change.newValue === "string" ? change.newValue.trim() : "";
      if (oldName === newName) {
        continue;
      }
      if (!oldName && newName) {
        rows.push({
          id: `${log.id}-${change.field}-add`,
          guest: newName,
          action: "Added",
          changedBy,
          date: formatAuditDate(log.timestamp),
        });
      } else if (oldName && !newName) {
        rows.push({
          id: `${log.id}-${change.field}-del`,
          guest: oldName,
          action: "Removed",
          changedBy,
          date: formatAuditDate(log.timestamp),
        });
      } else {
        rows.push({
          id: `${log.id}-${change.field}-edit`,
          guest: `${oldName} → ${newName}`,
          action: "Updated",
          changedBy,
          date: formatAuditDate(log.timestamp),
        });
      }
    }
  }
  return rows;
}
